import { cn } from "@gymflow/helpers";
import { Transition } from "@headlessui/react";
import { useContext, useState } from "react";

import { usePortalRoutes } from "../../../hooks";
import { useClubSettings } from "../../../providers";
import { AuthenticationContext } from "../../../providers/authentication/AuthenticationContext";
import { SessionOverlayContext } from "../../../providers/authentication/SessionOverlay/SessionOverlayContext";
import { generateSiteWithAuthRoutes } from "../../../routes";
import { Button, MenuIcon, NavLink, navLinkVariants } from "../../atoms";
import HostedPagesProfileAvatar from "./HostedPagesProfileAvatar";

export function HostedPagesNavbar() {
  const auth = useContext(AuthenticationContext);
  const { setSessionOverlayState } = useContext(SessionOverlayContext);
  const propertiesSettings = useClubSettings();

  const routesWithAuth = generateSiteWithAuthRoutes(propertiesSettings);

  const [isNavDropdownOpen, setIsNavDropdownOpen] = useState(false);
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const routesOrder = [
    "Memberships",
    "Credit Packs",
    "Products",
    "Gift Voucher",
    "Classes",
    "Appointments",
    "Contact Us",
  ];
  const routesForLinks = routesWithAuth.sort(
    (a, b) => routesOrder.indexOf(a.name) - routesOrder.indexOf(b.name),
  );
  return (
    <>
      <div className="bg-gray-0 flex items-center justify-between border-b border-gray-300 lg:hidden dark:border-gray-700 dark:bg-gray-950">
        <div className="flex w-full items-center justify-between py-2 pl-4 pr-3">
          <a
            className="flex justify-center"
            href={propertiesSettings.webSettings.yourWebsiteUrl}
          >
            <img
              src={propertiesSettings.webSettings.logo}
              alt="logo"
              className="h-8 object-contain"
            />
          </a>
          <div
            className="cursor-pointer p-2"
            onClick={() => {
              setIsNavDropdownOpen(!isNavDropdownOpen);
            }}
          >
            <MenuIcon />
          </div>
        </div>
      </div>
      <div
        onClick={() => {
          setIsNavDropdownOpen(false);
        }}
        className={cn(
          "pointer-events-none absolute inset-0 bottom-9 top-[calc(4.5rem+1px)] flex transition-all duration-200 lg:hidden",
          {
            "pointer-events-auto z-20 bg-gray-900/50 bg-opacity-50 backdrop-blur-sm":
              isNavDropdownOpen,
          },
        )}
      >
        <Transition
          show={isNavDropdownOpen}
          className="bg-gray-0 absolute inset-y-0 right-0 w-72 overflow-y-auto px-6 py-3 dark:bg-gray-950"
          enter="transition ease duration-300 transform"
          enterFrom="opacity-0 translate-x-12"
          enterTo="opacity-100 -translate-x-0"
          leave="transition ease duration-300 transform"
          leaveFrom="opacity-100 -translate-x-0"
          leaveTo="opacity-0 translate-x-12"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="flex flex-col gap-2">
            {propertiesSettings.webSettings.yourWebsiteUrl && (
              <a
                className={cn(navLinkVariants(), "p-3")}
                href={propertiesSettings.webSettings.yourWebsiteUrl}
              >
                Home
              </a>
            )}
            {routesForLinks.map((r) => {
              if (r.invisible) {
                return null;
              }
              return (
                <NavLink
                  className="p-3"
                  key={r.path}
                  to={createSiteOrEmbedLink(r.path)}
                >
                  {r.name}
                </NavLink>
              );
            })}
          </div>
          <div className="my-2 flex w-full border-b border-gray-300 dark:border-gray-700" />
          <div className="flex w-full flex-row justify-center gap-2 pt-2">
            {auth.userId ? (
              <HostedPagesProfileAvatar />
            ) : (
              <>
                <Button
                  className="flex-1"
                  onClick={async () => {
                    setSessionOverlayState({
                      screen: "sign-in",
                    });
                    setIsNavDropdownOpen(false);
                  }}
                >
                  <div className="flex-nowrap">Sign In</div>
                </Button>
                <Button
                  intent="secondary"
                  className="flex-1"
                  onClick={async () => {
                    setSessionOverlayState({
                      screen: "try-sign-in",
                    });
                    setIsNavDropdownOpen(false);
                  }}
                >
                  <div className="flex-nowrap">Sign Up</div>
                </Button>
              </>
            )}
          </div>
        </Transition>
      </div>
      <div className="bg-gray-0 hidden h-[3.875rem] items-center justify-between border-b border-gray-300 px-4 lg:flex lg:px-28 dark:border-gray-700 dark:bg-gray-950">
        {propertiesSettings.webSettings.logo ? (
          <div className="flex h-10 justify-start">
            {propertiesSettings.webSettings.yourWebsiteUrl ? (
              <a
                className="flex justify-center"
                href={propertiesSettings.webSettings.yourWebsiteUrl}
              >
                <img
                  src={propertiesSettings.webSettings.logo}
                  alt="logo"
                  className="h-8 object-contain"
                />
              </a>
            ) : (
              <img
                src={propertiesSettings.webSettings.logo}
                alt="logo"
                className="h-8 object-contain"
              />
            )}
          </div>
        ) : (
          <div className="bg-gymflow-logo h-10 w-48 bg-contain bg-center bg-no-repeat"></div>
        )}
        <div className="flex items-center justify-self-center">
          {propertiesSettings.webSettings.yourWebsiteUrl && (
            <a
              className={cn(navLinkVariants(), "p-3")}
              href={propertiesSettings.webSettings.yourWebsiteUrl}
            >
              Home
            </a>
          )}
          {routesForLinks.map((r) => {
            if (r.invisible) {
              return null;
            }
            return (
              <NavLink
                className="p-3"
                key={r.path}
                to={createSiteOrEmbedLink(r.path)}
              >
                {r.name}
              </NavLink>
            );
          })}
        </div>
        <div className="flex items-center gap-4">
          {auth.userId ? (
            <HostedPagesProfileAvatar />
          ) : (
            <>
              <Button
                onClick={() => {
                  setSessionOverlayState({
                    screen: "sign-in",
                  });
                }}
              >
                Sign In
              </Button>
              <Button
                intent="secondary"
                onClick={() => {
                  setSessionOverlayState({
                    screen: "try-sign-in",
                  });
                }}
              >
                Sign Up
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
